import { readActiveSession } from './sessionStorage.js';

const DEVICE_ID_KEY = 'casino.analytics.device_id';
const DEFAULT_FLUSH_AT = 20;
const DEFAULT_FLUSH_INTERVAL_MS = 10_000;

export const EVENT_NAMES = [
  'app_loaded',
  'app_visible',
  'app_hidden',
  'app_session_end',
  'lobby_table_clicked',
  'lobby_info_pill_opened',
  'table_entered',
  'table_exited',
  'bet_resolved',
  'craps_bet_placed',
  'craps_dice_rolled',
  'tutorial_opened',
  'tutorial_step_completed',
  'dev_panel_opened',
  'client_error',
] as const;

export type EventName = (typeof EVENT_NAMES)[number];

export interface BufferedEvent {
  name: EventName;
  client_ts: number;
  payload: Record<string, unknown>;
}

export interface EventsBatch {
  device_id: string;
  session_id: string;
  version?: string;
  events: BufferedEvent[];
}

export interface FeedbackPayload {
  device_id: string;
  session_id: string;
  version?: string;
  kind: 'feature' | 'bug';
  body: string;
  route?: string;
  replay_session_id?: string;
  replay_seq?: number;
}

export interface AnalyticsTransport {
  send: (batch: EventsBatch) => Promise<void>;
  beacon: (batch: EventsBatch) => boolean;
  sendFeedback?: (payload: FeedbackPayload) => Promise<void>;
}

export interface AnalyticsConfig {
  transport?: AnalyticsTransport;
  now?: () => number;
  flushAt?: number;
  flushIntervalMs?: number;
  endpoint?: string;
  feedbackEndpoint?: string;
  /** Build identifier (e.g., short git SHA). Attached to every events batch and feedback POST. */
  version?: string;
}

interface State {
  deviceId: string;
  sessionId: string;
  version: string | undefined;
  buffer: BufferedEvent[];
  transport: AnalyticsTransport;
  now: () => number;
  flushAt: number;
  flushTimerId: ReturnType<typeof setTimeout> | null;
  flushIntervalMs: number;
  feedbackEndpoint: string;
  /** Timestamp the tab last became visible. Null while hidden. */
  lastVisibleAt: number | null;
  /** Cumulative ms the tab has been visible during this session. */
  accumVisibleMs: number;
  visibilityListener: (() => void) | null;
  pagehideListener: (() => void) | null;
}

let state: State | null = null;

export function initAnalytics(config: AnalyticsConfig = {}): void {
  if (state) return;

  const transport = config.transport ?? defaultTransport(config.endpoint, config.feedbackEndpoint);
  const now = config.now ?? Date.now;
  const flushAt = config.flushAt ?? DEFAULT_FLUSH_AT;
  const intervalMs = config.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;

  state = {
    deviceId: ensureDeviceId(),
    sessionId: uuid(),
    version: config.version,
    buffer: [],
    transport,
    now,
    flushAt,
    flushTimerId: null,
    flushIntervalMs: intervalMs,
    feedbackEndpoint: config.feedbackEndpoint ?? '/api/feedback',
    lastVisibleAt:
      typeof document === 'undefined' || document.visibilityState !== 'hidden' ? now() : null,
    accumVisibleMs: 0,
    visibilityListener: null,
    pagehideListener: null,
  };

  // No timer on init — timer is started lazily on first track() call.

  if (typeof document !== 'undefined') {
    const onVisibilityChange = () => {
      if (!state) return;
      if (document.visibilityState === 'hidden') {
        const visibleMs = state.lastVisibleAt !== null ? state.now() - state.lastVisibleAt : 0;
        state.accumVisibleMs += visibleMs;
        state.lastVisibleAt = null;
        track('app_hidden', { visible_ms: visibleMs });
        beaconFlush();
      } else {
        state.lastVisibleAt = state.now();
        track('app_visible');
      }
    };
    const onPageHide = () => {
      if (!state) return;
      if (state.lastVisibleAt !== null) {
        state.accumVisibleMs += state.now() - state.lastVisibleAt;
        state.lastVisibleAt = null;
      }
      track('app_session_end', { active_ms: state.accumVisibleMs });
      beaconFlush();
    };
    state.visibilityListener = onVisibilityChange;
    state.pagehideListener = onPageHide;
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
  }
}

export function track(name: EventName, payload: Record<string, unknown> = {}): void {
  if (!state) return;
  state.buffer.push({ name, client_ts: state.now(), payload });
  if (state.buffer.length >= state.flushAt) {
    void flush();
  } else {
    scheduleFlushIfNeeded(state);
  }
}

export async function submitFeedback(
  kind: 'feature' | 'bug',
  body: string,
  route?: string,
): Promise<void> {
  if (!state) throw new Error('analytics not initialized');
  // Build payload, conditionally including `route` for `exactOptionalPropertyTypes`.
  const payload: FeedbackPayload = {
    device_id: state.deviceId,
    session_id: state.sessionId,
    kind,
    body,
  };
  if (typeof route === 'string') payload.route = route;
  if (typeof state.version === 'string') payload.version = state.version;
  const active = readActiveSession();
  if (active) {
    payload.replay_session_id = active.session_id;
    payload.replay_seq = active.last_seq;
  }
  if (state.transport.sendFeedback) {
    await state.transport.sendFeedback(payload);
    return;
  }
  await fetch(state.feedbackEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  });
}

export function getIdentity(): { device_id: string; session_id: string } | null {
  return state ? { device_id: state.deviceId, session_id: state.sessionId } : null;
}

function scheduleFlushIfNeeded(s: State): void {
  // Only schedule a new timer if one isn't already pending.
  if (s.flushTimerId !== null) return;
  s.flushTimerId = setTimeout(() => {
    s.flushTimerId = null;
    void flush();
  }, s.flushIntervalMs);
}

function buildBatch(s: State, events: BufferedEvent[]): EventsBatch {
  const batch: EventsBatch = { device_id: s.deviceId, session_id: s.sessionId, events };
  if (typeof s.version === 'string') batch.version = s.version;
  return batch;
}

async function flush(): Promise<void> {
  if (!state || state.buffer.length === 0) return;
  const s = state;
  const events = s.buffer;
  s.buffer = [];
  try {
    await s.transport.send(buildBatch(s, events));
  } catch {
    // drop on failure (per design)
  }
}

function beaconFlush(): void {
  if (!state || state.buffer.length === 0) return;
  const events = state.buffer;
  state.buffer = [];
  state.transport.beacon(buildBatch(state, events));
}

function ensureDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing && /^[0-9a-f-]{36}$/.test(existing)) return existing;
    const fresh = uuid();
    localStorage.setItem(DEVICE_ID_KEY, fresh);
    return fresh;
  } catch {
    return uuid();
  }
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 fallback
  const b = new Uint8Array(16);
  for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function defaultTransport(endpoint = '/api/events', feedbackEndpoint = '/api/feedback'): AnalyticsTransport {
  return {
    send: async (batch) => {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batch),
        keepalive: true,
      });
      if (!res.ok) throw new Error(`events send failed: ${res.status}`);
    },
    beacon: (batch) => {
      if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
      const blob = new Blob([JSON.stringify(batch)], { type: 'application/json' });
      return navigator.sendBeacon(endpoint, blob);
    },
    sendFeedback: async (payload) => {
      const res = await fetch(feedbackEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });
      if (!res.ok) throw new Error(`feedback send failed: ${res.status}`);
    },
  };
}

/** @internal — for tests only. */
export function __resetAnalyticsForTests(): void {
  if (state?.flushTimerId) clearTimeout(state.flushTimerId);
  if (state?.visibilityListener && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', state.visibilityListener);
  }
  if (state?.pagehideListener && typeof window !== 'undefined') {
    window.removeEventListener('pagehide', state.pagehideListener);
  }
  state = null;
}
